import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import {
	type ClipboardEvent,
	type FormEvent,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/shared/Modal';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import apiService, { RateLimitError } from '@/services/apiService';
import { validateEmail } from '@/services/validators';

interface ShareQuoteProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	policyUploadFormId?: string;
}

const ShareQuote = ({ open, onOpenChange, policyUploadFormId }: ShareQuoteProps) => {
	const { t } = useTranslation();
	const id = policyUploadFormId;

	const [tags, setTags] = useState<string[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [message, setMessage] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitResult, setSubmitResult] = useState<'success' | 'error' | 'rateLimit' | null>(null);
	const [skippedEmails, setSkippedEmails] = useState<string[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);

	const resetForm = useCallback(() => {
		setTags([]);
		setInputValue('');
		setMessage('');
		setError(null);
		setSubmitResult(null);
		setSkippedEmails([]);
	}, []);

	useEffect(() => {
		if (!open) {
			resetForm();
		}
	}, [open, resetForm]);

	useEffect(() => {
		if (submitResult !== 'success') return;
		const timer = setTimeout(() => {
			onOpenChange(false);
		}, 4000);
		return () => clearTimeout(timer);
	}, [submitResult, onOpenChange]);

	const tryAddEmail = (raw: string): boolean => {
		const email = raw.trim().toLowerCase();
		if (!email) return false;

		if (validateEmail(email)) {
			setError(t('modals.share.emailInvalid'));
			return false;
		}
		if (tags.includes(email)) {
			setError(t('modals.share.emailDuplicate'));
			return false;
		}
		if (tags.length >= 10) {
			return false;
		}

		setTags((prev) => [...prev, email]);
		setError(null);
		return true;
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
			if (inputValue.trim()) {
				e.preventDefault();
				if (tryAddEmail(inputValue)) {
					setInputValue('');
				}
			} else if (e.key === 'Enter') {
				e.preventDefault();
			}
		}

		if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
			setTags((prev) => prev.slice(0, -1));
			setError(null);
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		const pasted = e.clipboardData.getData('text');
		const parts = pasted.split(/[,;\s]+/).filter(Boolean);
		if (parts.length > 1) {
			e.preventDefault();
			const accumulated = [...tags];
			for (const part of parts) {
				if (accumulated.length >= 10) break;
				const email = part.trim().toLowerCase();
				if (email && !validateEmail(email) && !accumulated.includes(email)) {
					accumulated.push(email);
				}
			}
			if (accumulated.length > tags.length) {
				setTags(accumulated);
				setError(null);
			}
		}
	};

	const removeTag = (index: number) => {
		setTags((prev) => prev.filter((_, i) => i !== index));
		setError(null);
		inputRef.current?.focus();
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		let allTags = [...tags];
		if (inputValue.trim()) {
			if (tryAddEmail(inputValue)) {
				allTags = [...allTags, inputValue.trim().toLowerCase()];
				setInputValue('');
			} else {
				return;
			}
		}

		if (allTags.length === 0) {
			setError(t('modals.share.emailRequired'));
			return;
		}

		if (!id) return;

		setIsSubmitting(true);
		setSubmitResult(null);

		try {
			const result = await apiService.share.send(id, allTags, message || undefined);
			setSubmitResult('success');
			setSkippedEmails(result.skipped_emails ?? []);
		} catch (err) {
			if (err instanceof RateLimitError) {
				setSubmitResult('rateLimit');
			} else {
				setSubmitResult('error');
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal id="modalShareQuote" open={open} onOpenChange={onOpenChange} closeButton={true}>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-2">{t('modals.share.title')}</div>
				<p className="mb-4 text-muted-foreground leading-relaxed">
					{t('modals.share.description')}
				</p>

				{submitResult === 'success' ? (
					<div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-md">
						<CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
						<div className="text-xs text-green-700">
							<p>{t('modals.share.successMessage')}</p>
							{skippedEmails.length > 0 && (
								<p className="mt-1">
									{t('modals.share.skippedEmails', { emails: skippedEmails.join(', ') })}
								</p>
							)}
						</div>
					</div>
				) : (
					<div className="flex items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-md">
						<AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
						<p className="text-xs text-amber-700">{t('modals.share.sensitivityNotice')}</p>
					</div>
				)}

				<form onSubmit={handleSubmit} noValidate className="space-y-4 text-left">
					<Field data-invalid={!!error}>
						<FieldLabel>{t('modals.share.emailLabel')}</FieldLabel>
						<div
							role="group"
							className={`flex flex-wrap items-center gap-1.5 min-h-10 w-full rounded-md border bg-background px-2 py-1.5 text-sm ring-offset-background transition-colors focus-within:ring-3 focus-within:ring-neutral-100 ${
								error ? 'border-destructive' : 'border-input'
							}`}
							onClick={() => inputRef.current?.focus()}
							onKeyDown={() => inputRef.current?.focus()}
						>
							{tags.map((email, index) => (
								<span
									key={email}
									className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-sm text-foreground"
								>
									{email}
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											removeTag(index);
										}}
										disabled={isSubmitting}
										className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer rounded-sm"
										aria-label={t('modals.share.removeEmail', { email })}
									>
										<X className="w-3 h-3" />
									</button>
								</span>
							))}
							<input
								ref={inputRef}
								type="email"
								value={inputValue}
								onChange={(e) => {
									setInputValue(e.target.value);
									if (error) setError(null);
								}}
								onKeyDown={handleKeyDown}
								onPaste={handlePaste}
								onBlur={() => {
									if (inputValue.trim()) {
										if (tryAddEmail(inputValue)) {
											setInputValue('');
										}
									}
								}}
								disabled={isSubmitting || tags.length >= 10}
								placeholder={tags.length === 0 ? t('modals.share.emailPlaceholder') : ''}
								className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed py-0.5"
								data-testid="share-email-input"
								aria-invalid={!!error}
							/>
						</div>
						{error && <FieldError>{error}</FieldError>}
					</Field>

					<Field>
						<FieldLabel htmlFor="shareMessage">{t('modals.share.messageLabel')}</FieldLabel>
						<textarea
							id="shareMessage"
							placeholder={t('modals.share.messagePlaceholder')}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							disabled={isSubmitting}
							maxLength={500}
							rows={3}
							className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-neutral-100 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
							data-testid="share-message-input"
						/>
					</Field>

					{submitResult === 'error' && (
						<p className="text-sm text-destructive">{t('modals.share.errorMessage')}</p>
					)}
					{submitResult === 'rateLimit' && (
						<p className="text-sm text-destructive">{t('modals.share.rateLimitMessage')}</p>
					)}

					<Button
						type="submit"
						disabled={isSubmitting}
						className="w-full"
						iconRight={
							isSubmitting ? <Spinner size="sm" variant="current" visible={isSubmitting} /> : null
						}
						data-testid="share-submit-button"
					>
						{isSubmitting ? t('modals.share.submitting') : t('modals.share.submitButton')}
					</Button>
				</form>
			</div>
		</Modal>
	);
};

export default ShareQuote;
